// Stage 2 — Find Reviewers (FR-2.*, COI-*, RT-*). Rank/activate, mini-team, propose reviewers,
// and the headline: non-blocking Prophy COI with live "Checking… → Clear/Conflict" and a
// gated evaluate form.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  MINI_TEAM_MAX,
  type Domain,
  type PreprintView,
  type ProposalView,
  type RefereeView,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Spinner, statusTone } from "../../components/ui.js";
import { ReviewerProfilePanel } from "../preprints/ReviewerProfilePanel.js";
import { EvaluateModal } from "./EvaluateModal.js";
import { useFeatures } from "../../hooks/useFeatures.js";

export function ReviewersPortal(): JSX.Element {
  const { user } = useAuth();
  const [domain, setDomain] = useState<Domain>((user?.domains[0] ?? "BCS") as Domain);
  const [activeId, setActiveId] = useState<string | null>(null);

  const { data: preprints, isLoading } = useQuery({
    queryKey: qk.preprints({ domain }),
    queryFn: () => api.get<PreprintView[]>(`/preprints?domain=${domain}`),
  });

  useEffect(() => {
    if (!activeId && preprints?.length) {
      // FR-N5: statuses are global again, so "the active one" means exactly that.
      setActiveId(preprints.find((p) => p.status === "ACTIVE")?.id ?? preprints[0]!.id);
    }
  }, [preprints, activeId]);

  const active = preprints?.find((p) => p.id === activeId) ?? null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Find Reviewers</h1>
          <p className="text-sm text-slate-500">Rank &amp; activate preprints, then propose reviewers and run COI checks.</p>
        </div>
        <Select value={domain} onChange={(e) => { setDomain(e.target.value as Domain); setActiveId(null); }}>
          {DOMAINS.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </Select>
      </header>

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <PipelinePanel preprints={preprints ?? []} loading={isLoading} domain={domain} activeId={activeId} onPick={setActiveId} />
        {active ? <ReviewerWorkbench preprint={active} /> : <EmptyState>Pick a preprint to find reviewers.</EmptyState>}
      </div>
    </div>
  );
}

function PipelinePanel({
  preprints,
  loading,
  domain,
  activeId,
  onPick,
}: {
  preprints: PreprintView[];
  loading: boolean;
  domain: Domain;
  activeId: string | null;
  onPick: (id: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const ctx = { domain };
  // FR-K9 sort, on the GLOBAL status (FR-N5 reverts FR-L10's viewer-relative ordering): Active →
  // On Hold → Dormant. The DB's rank order is kept within each group (Array.sort is stable).
  const STATUS_ORDER: Record<string, number> = { ACTIVE: 0, "ON HOLD": 1, DORMANT: 2 };
  const sorted = [...preprints].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));
  const rank = useMutation({
    mutationFn: ({ id, rank }: { id: string; rank: number }) => api.post(`/preprints/${id}/rank`, { rank }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preprints"] }),
  });
  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/preprints/${id}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preprints"] }),
  });

  return (
    <Card className="h-fit">
      <CardHeader>
        <h2 className="text-sm font-semibold">Pipeline — {domain}</h2>
        <p className="text-xs text-slate-500">One active preprint per domain (FR-2.2).</p>
      </CardHeader>
      <CardBody className="space-y-2">
        {loading ? (
          <Spinner label="Loading…" />
        ) : sorted.length === 0 ? (
          <EmptyState>No preprints in this domain yet.</EmptyState>
        ) : (
          sorted.map((p) => (
            <div
              key={p.id}
              className={`rounded-md border p-2 ${p.id === activeId ? "border-brand bg-brand/5" : "border-slate-200"}`}
            >
              <button className="block w-full text-left" onClick={() => onPick(p.id)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{p.title}</span>
                  {/* FR-N5: the TRUE global status — a label must not shift with who is looking.
                      "Is this one mine?" is the Mini team badge's job, not the status'. */}
                  <div className="flex shrink-0 items-center gap-1">
                    <Badge tone={statusTone(p.status)}>{p.status}</Badge>
                    <MiniTeamBadge count={p.miniTeamEmails.length} />
                  </div>
                </div>
                {/* July-7 FR-K11: unchecked (AE-unchecked) sits beside proposed + greenlit so an
                    admin can spot an AE who's behind; AE name + mini-team COUNT for everyone. */}
                <div className="mt-0.5 text-xs text-slate-500">
                  proposed {p.proposedCount} · unchecked {p.uncheckedCount} · greenlit {p.greenlitCount}
                  {p.rank != null && ` · rank ${p.rank}`}
                </div>
                <div className="text-xs text-slate-500">
                  {p.assignedEditorName || p.assignedEditorEmail
                    ? `AE ${p.assignedEditorName ?? p.assignedEditorEmail}`
                    : "No AE assigned"}
                  {` · mini team ${p.miniTeamEmails.length}`}
                </div>
              </button>
              <div className="mt-1.5 flex gap-1.5">
                {can("rankPreprints", ctx) && (
                  <Input
                    type="number"
                    min={1}
                    placeholder="rank"
                    defaultValue={p.rank ?? undefined}
                    className="w-16 py-1"
                    onBlur={(e) => e.target.value && rank.mutate({ id: p.id, rank: Number(e.target.value) })}
                  />
                )}
                {p.status !== "ACTIVE" && can("activatePreprint", ctx) && (
                  <Button variant="secondary" className="py-1" onClick={() => activate.mutate(p.id)}>
                    Activate
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
}

/** FR-N5: marks a preprint that has mini-team members — the badge that replaced the relabel. */
function MiniTeamBadge({ count }: { count: number }): JSX.Element | null {
  if (count === 0) return null;
  return <Badge tone="violet">Mini team {count}</Badge>;
}

function ReviewerWorkbench({ preprint }: { preprint: PreprintView }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { features } = useFeatures();
  const { user } = useAuth();
  const [evalProposal, setEvalProposal] = useState<ProposalView | null>(null);
  // FR-N6: the proposed list defaults to the student's OWN proposals; "All proposed" reveals the
  // full list, rendered BELOW the Prophy candidates (the mockup put it above — the FR wins).
  const [showAllProposed, setShowAllProposed] = useState(false);

  const ctx = {
    domain: preprint.domain,
    preprint: { miniTeamEmails: preprint.miniTeamEmails, assignedEditorEmail: preprint.assignedEditorEmail },
  };

  const { data: referees } = useQuery({
    queryKey: qk.referees(preprint.id),
    queryFn: () => api.get<RefereeView[]>(`/referees?preprintId=${preprint.id}&status=Unclaimed`),
  });
  const { data: proposals } = useQuery({
    queryKey: qk.proposals({ preprintId: preprint.id }),
    queryFn: () => api.get<ProposalView[]>(`/proposals?preprintId=${preprint.id}`),
    refetchInterval: 10000, // belt-and-suspenders behind SSE (RT-4)
  });
  // FR-N6: split by proposer so "my reviewers" is the default view and the rest sits behind the
  // toggle. Filtering the one payload (rather than a second `mine=1` fetch) keeps both lists live
  // off a single SSE invalidation — the server already scopes WHICH preprints a student can see.
  const me = user?.email.toLowerCase();
  const mine = (proposals ?? []).filter((p) => p.proposerEmail?.toLowerCase() === me);
  const others = (proposals ?? []).filter((p) => p.proposerEmail?.toLowerCase() !== me);

  const claim = useMutation({
    mutationFn: ({ refereeId, email }: { refereeId: string; email: string }) =>
      api.post<ProposalView>(`/referees/${refereeId}/claim`, { preprintId: preprint.id, reviewerEmail: email }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["referees"] });
      qc.invalidateQueries({ queryKey: ["proposals"] });
      setEvalProposal(created); // FR-L3: Part 2 opens right after a claim too
    },
  });
  const submitCoi = useMutation({
    mutationFn: (proposalId: string) => api.post(`/coi`, { proposalId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  });
  const studentReject = useMutation({
    mutationFn: (id: string) => api.post(`/proposals/${id}/student-reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold">{preprint.title}</h2>
              {/* FR-N5: same stable status + Mini-team badge the pipeline card shows. */}
              <Badge tone={statusTone(preprint.status)}>{preprint.status}</Badge>
              <MiniTeamBadge count={preprint.miniTeamEmails.length} />
            </div>
            {/* FR-L6: the detail header carries the same set as the pipeline card. */}
            <p className="text-xs text-slate-500">
              {preprint.domain} · proposed {preprint.proposedCount} · unchecked {preprint.uncheckedCount} · greenlit{" "}
              {preprint.greenlitCount}
            </p>
            <p className="text-xs text-slate-500">
              {preprint.assignedEditorName || preprint.assignedEditorEmail
                ? `AE ${preprint.assignedEditorName ?? preprint.assignedEditorEmail}`
                : "No AE assigned"}
              {` · mini team ${preprint.miniTeamEmails.length}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* WO B3 entry point: the article-details reviewer/COI surface (FEATURE_COI-gated). */}
            {features.coi.enabled && (
              <Link className="text-sm text-brand hover:underline" to={`/preprints/${preprint.id}/reviewers`}>
                Reviewers &amp; COI
              </Link>
            )}
            {preprint.doi && (
              <a className="text-sm text-brand hover:underline" href={preprint.doi} target="_blank" rel="noreferrer">
                Open DOI
              </a>
            )}
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          {/* AE-half reviewer profile, visible to everyone finding reviewers (FR-J11). */}
          {preprint.selectedId && (
            <details open className="rounded-md border border-slate-100 p-2">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                Reviewer profile (who we’re looking for)
              </summary>
              <div className="mt-2">
                <ReviewerProfilePanel selectedId={preprint.selectedId} />
              </div>
            </details>
          )}
          <ManualPropose
            preprintId={preprint.id}
            canPropose={can("claimEvaluateSuggestion", ctx)}
            onProposed={setEvalProposal} // FR-L3: Part-2 form opens right after Propose
          />
          {can("manageMiniTeam", ctx) && <MiniTeamEditor preprint={preprint} />}
        </CardBody>
      </Card>

      {/* FR-N6 order: your own reviewers first (the default), then the Prophy candidates, and only
          then — behind the toggle — everyone else's. The two proposal paths (claim a Prophy
          candidate / type one in above) both land in "My reviewers". */}
      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Proposed reviewers — mine ({mine.length})</h3>
          <Button variant="ghost" className="py-1" onClick={() => setShowAllProposed((v) => !v)} aria-pressed={showAllProposed}>
            {showAllProposed ? "Hide all proposed" : `All proposed (${(proposals ?? []).length})`}
          </Button>
        </CardHeader>
        <CardBody className="space-y-2">
          {mine.length === 0 && <EmptyState>You haven’t proposed anyone for this preprint yet.</EmptyState>}
          {mine.map((p) => (
            <ProposalRow
              key={p.id}
              p={p}
              canSubmit={can("submitCoiCheck", ctx)}
              canEvaluate={can("completeEvaluateForm", ctx)}
              onSubmitCoi={() => submitCoi.mutate(p.id)}
              onEvaluate={() => setEvalProposal(p)}
              onStudentReject={() => studentReject.mutate(p.id)}
            />
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Prophy candidates</h3>
        </CardHeader>
        <CardBody className="space-y-2">
          {(referees ?? []).length === 0 && <EmptyState>No unclaimed candidates.</EmptyState>}
          {(referees ?? []).slice(0, 10).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 p-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{r.name}</div>
                <div className="text-xs text-slate-500">
                  {r.affiliations ?? "—"} · H-index {r.hIndex ?? "?"}
                </div>
              </div>
              {can("claimEvaluateSuggestion", ctx) && (
                <Button
                  variant="secondary"
                  className="py-1"
                  onClick={() => {
                    const email = window.prompt(`Reviewer email for ${r.name}?`) ?? "";
                    if (email) claim.mutate({ refereeId: r.id, email });
                  }}
                >
                  Claim
                </Button>
              )}
            </div>
          ))}
        </CardBody>
      </Card>

      {showAllProposed && (
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold">All proposed reviewers ({(proposals ?? []).length})</h3>
            <p className="text-xs text-slate-500">Everyone’s proposals for this preprint, yours included.</p>
          </CardHeader>
          <CardBody className="space-y-2">
            {(proposals ?? []).length === 0 && <EmptyState>No proposals yet.</EmptyState>}
            {[...mine, ...others].map((p) => (
              <ProposalRow
                key={p.id}
                p={p}
                canSubmit={can("submitCoiCheck", ctx)}
                canEvaluate={can("completeEvaluateForm", ctx)}
                onSubmitCoi={() => submitCoi.mutate(p.id)}
                onEvaluate={() => setEvalProposal(p)}
                onStudentReject={() => studentReject.mutate(p.id)}
              />
            ))}
          </CardBody>
        </Card>
      )}

      {evalProposal && <EvaluateModal proposal={evalProposal} onClose={() => setEvalProposal(null)} />}
    </div>
  );
}

function coiLabel(p: ProposalView): { text: string; tone: Parameters<typeof Badge>[0]["tone"] } {
  // Must precede the "clear" branch: an unable_to_evaluate row also has coiStatus
  // "Prophy Checked" && !evaluateLocked and would otherwise render green "Clear".
  if (p.coiVerdict === "unable_to_evaluate") return { text: "Unable to evaluate", tone: "slate" };
  if (p.coiVerdict === "conflict" || p.status === "COI Detected")
    return { text: `Conflict${p.coiSummary ? `: ${p.coiSummary}` : ""}`, tone: "red" };
  if (p.coiVerdict === "clear" || (p.coiStatus === "Prophy Checked" && !p.evaluateLocked))
    return { text: "Clear — evaluate now", tone: "green" };
  if (p.coiPending) return { text: "Checking…", tone: "amber" };
  return { text: "COI not run", tone: "slate" };
}

function ProposalRow({
  p,
  canSubmit,
  canEvaluate,
  onSubmitCoi,
  onEvaluate,
  onStudentReject,
}: {
  p: ProposalView;
  canSubmit: boolean;
  canEvaluate: boolean;
  onSubmitCoi: () => void;
  onEvaluate: () => void;
  onStudentReject: () => void;
}): JSX.Element {
  const coi = coiLabel(p);
  const resolvedClear = !p.evaluateLocked;
  const notRun = !p.coiPending && p.coiVerdict == null && p.coiStatus !== "Prophy Checked";
  return (
    <div className="rounded-md border border-slate-200 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {p.firstName} {p.lastName}
          </div>
          <div className="text-xs text-slate-500">{p.reviewerEmail || "no email"} · {p.affiliation ?? "—"}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {p.incomplete && <Badge tone="amber">Part 2 pending</Badge>}
          <Badge tone={statusTone(p.status)}>{p.status}</Badge>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Badge tone={coi.tone}>{coi.text}</Badge>
        <div className="ml-auto flex gap-1.5">
          {notRun && canSubmit && (
            <Button className="py-1" onClick={onSubmitCoi}>
              Submit COI
            </Button>
          )}
          {/* July-8 FR-L3: every proposal (claimed or manual) goes through the Part-2 form. */}
          {resolvedClear && p.status !== "Reject" && canEvaluate && (
            <Button className="py-1" onClick={onEvaluate}>
              {p.incomplete ? "Evaluate" : "Re-evaluate"}
            </Button>
          )}
          <Button variant="ghost" className="py-1" onClick={onStudentReject} title="Student reject">
            ✕
          </Button>
        </div>
      </div>
    </div>
  );
}

// COI-first manual proposal (July-2 FR-J12): the walls run server-side; a 409 shows the block
// reason (author / duplicate / recent / same-affiliation→Backup). Passing = complete on submit.
const EMPTY_PROPOSE = { firstName: "", lastName: "", reviewerEmail: "", affiliation: "", backupReviewer: false, affiliationCoi: false };

function ManualPropose({
  preprintId,
  canPropose,
  onProposed,
}: {
  preprintId: string;
  canPropose: boolean;
  /** July-8 FR-L3: called with the created (incomplete) proposal so the Part-2 form opens. */
  onProposed?: (p: ProposalView) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY_PROPOSE);
  const [wallError, setWallError] = useState<{ error: string; requireBackup?: boolean } | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<ProposalView>(`/proposals`, { preprintId, ...f }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      setF(EMPTY_PROPOSE);
      setWallError(null);
      onProposed?.(created);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object") {
        setWallError(err.body as { error: string; requireBackup?: boolean });
      } else {
        setWallError({ error: "Could not propose this reviewer." });
      }
    },
  });
  if (!canPropose) return <p className="text-xs text-slate-500">You don&apos;t have access to propose for this preprint.</p>;
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Input placeholder="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} required />
        <Input placeholder="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} required />
        <Input placeholder="Email" type="email" value={f.reviewerEmail} onChange={(e) => setF({ ...f, reviewerEmail: e.target.value })} required />
        <Input placeholder="Affiliation" value={f.affiliation} onChange={(e) => setF({ ...f, affiliation: e.target.value })} required />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={f.backupReviewer} onChange={(e) => setF({ ...f, backupReviewer: e.target.checked })} />
          Backup Reviewer?
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={f.affiliationCoi} onChange={(e) => setF({ ...f, affiliationCoi: e.target.checked })} />
          Affiliation COI
        </label>
        <Button type="submit" disabled={create.isPending} className="ml-auto">
          {create.isPending ? "Running COI checks…" : "Propose Reviewer"}
        </Button>
      </div>
      {wallError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" role="alert">
          <p>{wallError.error}</p>
          {wallError.requireBackup && <p className="mt-1 font-medium">Tick “Backup Reviewer?” to propose them as a backup.</p>}
        </div>
      )}
    </form>
  );
}

// Update Mini-Team as 5 fixed slots (July-2 FR-J10); adding a member grants Dormant access
// and fires the join notification server-side.
function MiniTeamEditor({ preprint }: { preprint: PreprintView }): JSX.Element {
  const qc = useQueryClient();
  const [slots, setSlots] = useState<string[]>(() => {
    const s = [...preprint.miniTeamEmails];
    while (s.length < MINI_TEAM_MAX) s.push("");
    return s.slice(0, MINI_TEAM_MAX);
  });
  const save = useMutation({
    mutationFn: () => api.put(`/preprints/${preprint.id}/mini-team`, { emails: slots.map((s) => s.trim()).filter(Boolean) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preprints"] }),
  });
  return (
    <details className="rounded-md border border-slate-100 p-2">
      <summary className="cursor-pointer text-xs font-semibold text-slate-600">
        Mini-team ({preprint.miniTeamEmails.length}/{MINI_TEAM_MAX})
      </summary>
      <div className="mt-2 space-y-1.5">
        {slots.map((email, i) => (
          <Input
            key={i}
            type="email"
            placeholder={`Slot ${i + 1} — student email`}
            value={email}
            onChange={(e) => setSlots((s) => s.map((v, j) => (j === i ? e.target.value : v)))}
          />
        ))}
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Members get pre-activation (Dormant) access + an email.</p>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save mini-team"}
          </Button>
        </div>
      </div>
    </details>
  );
}

// EvaluateModal (the Part-2 reviewer-profile form) lives in ./EvaluateModal.tsx — shared with
// the Details-page ProposeReviewer flow since July-8 FR-L3.
